import { filterSchema } from 'pl-api';
import * as v from 'valibot';

import { locales } from '@/messages';

import { coerceObject, filteredArray, filteredRecord } from './utils';

import type { ITimelinePicker } from '@/components/timeline-picker';

const AVAILABLE_NAVIGATION_ITEMS = [
  'separator',
  'search-input',
  'home',
  'search',
  'notifications',
  'chats',
  'conversations',
  'groups',
  'profile',
  'drive',
  'settings',
  'dashboard',
  'public-timeline',
  'bubble-timeline',
  'fediverse-timeline',
  'wrenched-timeline',
  'follow-requests',
  'interaction-requests',
  'bookmarks',
  'favourites',
  'lists',
  'circles',
  'antennas',
  'collections',
  'events',
  'directory',
  'followed-hashtags',
  'rss-feed-subscriptions',
  'scheduled-statuses',
  'drafts',
  'edit-profile',
  'mutes',
  'blocks',
  'filters',
  'domain-blocks',
  'announcements',
  'birthdays',
  'circle',
  'compose',
  'deck',
] as const;

const DEFAULT_PINNED_NAVIGATION_ITEMS = [
  'home',
  'groups',
  'search',
  'notifications',
  'chats',
  'compose',
] as const;

const DEFAULT_NAVIGATION_ITEMS = [
  'search-input',
  'home',
  'search',
  'notifications',
  'chats',
  'groups',
  'profile',
  'drive',
  'separator',
  'public-timeline',
  'bubble-timeline',
  'fediverse-timeline',
  'separator',
  'settings',
  'dashboard',
  'compose',
] as const;

const AVAILABLE_STATUS_ACTION_BAR_ITEMS = [
  'reply',
  'reblog',
  'quote',
  'favourite',
  'dislike',
  'wrench',
  'reaction',
  'quick-reactions',
  'bookmark',
  'share',
  'translate',
  'bite',
] as const;

const DEFAULT_STATUS_ACTION_BAR_ITEMS = [
  'reply',
  'reblog',
  'favourite',
  'dislike',
  'reaction',
] as const;

const AVAILABLE_SIDEBAR_ITEMS = [
  'context',
  'announcements',
  'recommendations',
  'promo',
  'footer',
  'compose',
  'notifications',
  'search',
  'profile-switcher',
  'shoutbox',
] as const;

const DEFAULT_SIDEBAR_ITEMS = [
  'context',
  'announcements',
  'recommendations',
  'promo',
  'footer',
] as const;

type SidebarItem =
  | (typeof AVAILABLE_SIDEBAR_ITEMS)[number]
  | 'compose:open-interactions'
  | `account:${string}`;

type NavigationItem =
  | (typeof AVAILABLE_NAVIGATION_ITEMS)[number]
  | `${'account' | 'list' | 'circle' | 'antenna' | 'instance' | 'hashtag' | 'bookmark_folder'}:${string}`;

const NAVIGATION_ITEM_PREFIXES = [
  'account',
  'list',
  'circle',
  'antenna',
  'instance',
  'hashtag',
  'bookmark_folder',
];

const navigationItemSchema = v.custom<NavigationItem>(
  (item) =>
    typeof item === 'string' &&
    (AVAILABLE_NAVIGATION_ITEMS.includes(item as 'separator') ||
      NAVIGATION_ITEM_PREFIXES.some((prefix) => item.startsWith(prefix + ':'))),
);

const sidebarItemSchema = v.custom<SidebarItem>(
  (item) =>
    typeof item === 'string' &&
    (AVAILABLE_SIDEBAR_ITEMS.includes(item as 'context') ||
      item === 'compose:open-interactions' ||
      item.startsWith('account:')),
);

const timelineSchema = v.fallback(
  v.pipe(
    v.string(),
    v.transform<any, ITimelinePicker['active']>((timeline) => {
      if (['home', 'local', 'bubble', 'federated', 'wrenched'].includes(timeline)) {
        return timeline;
      }
      if (
        ['list', 'circle', 'antenna', 'instance'].some((prefix) =>
          timeline.startsWith(prefix + ':'),
        )
      ) {
        return timeline;
      }
      return 'home';
    }),
  ),
  'home',
);

const defaultTimelineSchema = v.fallback(
  v.pipe(
    v.string(),
    v.transform((timeline) => {
      if (timeline === 'deck') return timeline;
      if (['home', 'local', 'bubble', 'federated', 'wrenched'].includes(timeline)) {
        return timeline;
      }
      if (
        ['list', 'circle', 'antenna', 'instance'].some((prefix) =>
          timeline.startsWith(prefix + ':'),
        )
      ) {
        return timeline;
      }
      return 'home';
    }),
  ),
  'home',
);

const timelineFiltersSchema = coerceObject({
  showReblogs: v.fallback(v.boolean(), true),
  showSelfReblogs: v.fallback(v.boolean(), true),
  showReplies: v.fallback(v.boolean(), true),
  showQuotes: v.fallback(v.boolean(), true),
  showDirect: v.fallback(v.boolean(), true),
  showNonMedia: v.fallback(v.boolean(), true),
  showMediaWithoutAltText: v.fallback(v.boolean(), true),
  hideFollowedReposts: v.fallback(v.nullable(v.number()), null),
});

const baseDeckColumnSchema = v.object({
  id: v.fallback(v.string(), () => crypto.randomUUID()),
  columnWidth: v.fallback(v.picklist(['xs', 'sm', 'md', 'lg', 'xl']), 'md'),
  fillAvailableWidth: v.fallback(v.boolean(), false),
  accountUrl: v.fallback(v.optional(v.string()), undefined),
  pinned: v.fallback(v.boolean(), false),
});

const timelineDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('timeline'),
  timeline: timelineSchema,
  filters: v.fallback(v.optional(timelineFiltersSchema), undefined),
});

const notificationsDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('notifications'),
  filter: v.fallback(
    v.picklist(['all', 'mention', 'favourite', 'reblog', 'poll', 'status', 'follow', 'events']),
    'all',
  ),
});

const accountDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('account'),
  accountId: v.fallback(v.optional(v.string()), undefined),
  excludeReplies: v.fallback(v.boolean(), false),
  excludeReblogs: v.fallback(v.boolean(), false),
  showPinned: v.fallback(v.boolean(), false),
});

const searchDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('search'),
  query: v.fallback(v.string(), ''),
  searchType: v.fallback(v.picklist(['accounts', 'statuses', 'hashtags']), 'accounts'),
  accountId: v.fallback(v.optional(v.string()), undefined),
});

const trendingDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('trending'),
  trendsType: v.fallback(v.picklist(['accounts', 'statuses', 'hashtags', 'links']), 'hashtags'),
});

const bookmarksDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('bookmarks'),
  folderId: v.fallback(v.string(), 'all'),
});

const hashtagDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('hashtag'),
  hashtag: v.fallback(v.optional(v.string()), undefined),
  any: v.fallback(v.array(v.string()), []),
  all: v.fallback(v.array(v.string()), []),
  none: v.fallback(v.array(v.string()), []),
  filters: v.fallback(v.optional(timelineFiltersSchema), undefined),
});

const chatDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('chat'),
  chatId: v.string(),
});

const driveDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('drive'),
  folderId: v.fallback(v.optional(v.string()), undefined),
});

const composeDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.literal('compose'),
  openInteractions: v.fallback(v.boolean(), false),
});

const genericDeckColumnSchema = v.object({
  ...baseDeckColumnSchema.entries,
  type: v.picklist(['chats', 'scheduled', 'drafts', 'interaction-requests']),
});

const deckColumnSchema = v.variant('type', [
  timelineDeckColumnSchema,
  notificationsDeckColumnSchema,
  accountDeckColumnSchema,
  searchDeckColumnSchema,
  trendingDeckColumnSchema,
  bookmarksDeckColumnSchema,
  hashtagDeckColumnSchema,
  chatDeckColumnSchema,
  driveDeckColumnSchema,
  composeDeckColumnSchema,
  genericDeckColumnSchema,
]);

const createDefaultDeckColumns = (): Array<v.InferOutput<typeof deckColumnSchema>> => [
  {
    id: crypto.randomUUID(),
    type: 'compose',
    columnWidth: 'sm',
    fillAvailableWidth: false,
    pinned: true,
    openInteractions: true,
  },
  {
    id: crypto.randomUUID(),
    type: 'timeline',
    columnWidth: 'lg',
    fillAvailableWidth: false,
    pinned: false,
    timeline: 'home',
  },
  {
    id: crypto.randomUUID(),
    type: 'notifications',
    columnWidth: 'md',
    fillAvailableWidth: false,
    pinned: false,
    filter: 'all',
  },
  {
    id: crypto.randomUUID(),
    type: 'account',
    columnWidth: 'md',
    fillAvailableWidth: false,
    pinned: false,
    accountId: 'self',
    excludeReplies: false,
    excludeReblogs: false,
    showPinned: false,
  },
];

const deckLayoutSchema = v.object({
  id: v.fallback(v.string(), () => crypto.randomUUID()),
  name: v.fallback(v.string(), ''),
  columns: filteredArray(deckColumnSchema),
});

const deckSettingsSchema = v.fallback(
  v.pipe(
    v.object({
      layouts: v.fallback(v.optional(v.array(deckLayoutSchema)), undefined),
      columns: v.fallback(v.optional(filteredArray(deckColumnSchema)), undefined),
      activeLayout: v.fallback(v.optional(v.string()), undefined),
      mobileFullWidth: v.fallback(v.boolean(), false),
    }),
    v.transform(({ layouts, columns, activeLayout, mobileFullWidth }) => {
      const normalizedLayouts =
        layouts && layouts.length
          ? layouts
          : [{ id: crypto.randomUUID(), name: '', columns: columns ?? createDefaultDeckColumns() }];
      const active =
        activeLayout && normalizedLayouts.some((layout) => layout.id === activeLayout)
          ? activeLayout
          : normalizedLayouts[0].id;

      return { layouts: normalizedLayouts, activeLayout: active, mobileFullWidth };
    }),
  ),
  () => {
    const id = crypto.randomUUID();

    return {
      mobileFullWidth: false,
      activeLayout: id,
      layouts: [{ id, name: '', columns: createDefaultDeckColumns() }],
    };
  },
);

const skinToneSchema = v.picklist([1, 2, 3, 4, 5, 6]);

const baseOpenshockHookSchema = v.object({
  actionType: v.picklist(['Shock', 'Vibrate', 'Sound']),
});

const notificationOpenshockHookSchema = v.object({
  ...baseOpenshockHookSchema.entries,
  type: v.literal('notification'),
  notificationTypes: v.array(v.string()),
  intensity: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100)),
  duration: v.pipe(v.number(), v.integer(), v.minValue(300), v.maxValue(30000)),
});

const wrenchOpenshockHookSchema = v.object({
  ...baseOpenshockHookSchema.entries,
  type: v.literal('wrench'),
  adaptive: v.fallback(v.boolean(), false),
  minIntensity: v.fallback(v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100)), 0),
  maxIntensity: v.fallback(v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100)), 100),
  minDuration: v.fallback(v.pipe(v.number(), v.integer(), v.minValue(300), v.maxValue(65535)), 500),
  maxDuration: v.fallback(
    v.pipe(v.number(), v.integer(), v.minValue(300), v.maxValue(65535)),
    2000,
  ),
});

const replyOpenshockHookSchema = v.object({
  ...baseOpenshockHookSchema.entries,
  type: v.literal('reply'),
  intensity: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100)),
  duration: v.pipe(v.number(), v.integer(), v.minValue(300), v.maxValue(65535)),
  keyword: v.fallback(v.string(), ''),
  wholeWord: v.fallback(v.boolean(), false),
});

const openshockHookSchema = v.variant('type', [
  notificationOpenshockHookSchema,
  wrenchOpenshockHookSchema,
  replyOpenshockHookSchema,
]);

const settingsSchema = v.object({
  onboarded: v.fallback(v.boolean(), false),
  skinTone: v.fallback(skinToneSchema, 1),
  reduceMotion: v.fallback(v.boolean(), false),
  renderMfm: v.fallback(v.boolean(), true),
  renderAdvancedMfm: v.fallback(v.boolean(), true),
  renderAnimatedMfm: v.fallback(v.boolean(), false),
  underlineLinks: v.fallback(v.boolean(), false),
  absoluteTimestamps: v.fallback(v.boolean(), false),
  autoPlayGif: v.fallback(v.boolean(), true),
  disableVideoLooping: v.fallback(v.boolean(), true),
  displayMedia: v.fallback(v.picklist(['default', 'hide_all', 'show_all']), 'default'),
  displayPreviewCards: v.fallback(v.picklist(['default', 'hide', 'hide_media']), 'default'),
  displaySpoilers: v.fallback(v.boolean(), false),
  highlightSpoilers: v.fallback(v.boolean(), false),
  unfollowModal: v.fallback(v.boolean(), true),
  boostModal: v.fallback(v.boolean(), false),
  deleteModal: v.fallback(v.boolean(), true),
  missingDescriptionModal: v.fallback(v.boolean(), true),
  wrenchModal: v.fallback(v.boolean(), false),
  missingDescriptionBoostModal: v.fallback(v.boolean(), false),
  missingLanguageModal: v.fallback(v.boolean(), false),
  ignoreHashtagCasingSuggestions: v.fallback(v.boolean(), false),
  defaultPrivacy: v.fallback(v.picklist(['public', 'unlisted', 'private', 'direct']), 'public'),
  defaultContentType: v.fallback(
    v.picklist(['text/plain', 'text/markdown', 'text/html', 'wysiwyg']),
    'text/plain',
  ),
  defaultLanguage: v.fallback(v.nullable(v.string()), 'detect'),
  themeMode: v.fallback(v.picklist(['system', 'light', 'dark', 'black']), 'system'),
  locale: v.fallback(
    v.fallback(
      v.pipe(v.fallback(v.string(), navigator.language), v.picklist(locales)),
      navigator.language.split(/[-_]/)[0] as 'en',
    ),
    'en',
  ),
  showExplanationBox: v.fallback(v.boolean(), true),
  explanationBox: v.fallback(v.boolean(), true),
  autoloadTimelines: v.fallback(v.boolean(), false),
  autoloadMore: v.fallback(v.boolean(), false),
  preserveSpoilers: v.fallback(v.boolean(), true),
  prependReToSpoilers: v.fallback(v.boolean(), false),
  forceImplicitAddressing: v.fallback(v.boolean(), false),
  useDedicatedComposePage: v.fallback(v.boolean(), false),
  autosaveDrafts: v.fallback(v.boolean(), false),
  autoTranslate: v.fallback(v.boolean(), false),
  knownLanguages: v.fallback(v.array(v.string()), []),
  showSideBySideTranslations: v.fallback(v.boolean(), false),
  urlPrivacy: coerceObject({
    clearLinksInCompose: v.optional(v.boolean(), true),
    clearLinksInContent: v.optional(v.boolean(), false),
    allowReferralMarketing: v.optional(v.boolean(), false),
    rulesUrl: v.optional(v.string(), ''),
    hashUrl: v.optional(v.string(), ''),
    displayTargetHost: v.optional(v.boolean(), true),
    redirectLinksMode: v.optional(v.picklist(['off', 'auto', 'manual']), 'off'),
    redirectServicesUrl: v.optional(v.string(), ''),
    redirectServices: v.optional(v.record(v.string(), v.string()), {}),
  }),
  checkEmojiReactsSupport: v.fallback(v.boolean(), false),
  disableUserProvidedMedia: v.fallback(v.boolean(), false),
  stripMetadata: v.fallback(v.boolean(), false),
  storeSettingsInNotes: v.fallback(v.boolean(), false),
  composeInTimelines: v.fallback(v.boolean(), true),
  rememberTimelinePosition: v.fallback(v.boolean(), true),
  accountNicknames: v.fallback(v.record(v.string(), v.string()), {}),
  useSystemMediaControls: v.fallback(v.boolean(), false),
  displayMentionAvatars: v.fallback(v.boolean(), true),
  defaultTimeline: defaultTimelineSchema,
  showChatWidget: v.fallback(v.boolean(), true),
  showNestedQuotes: v.fallback(v.boolean(), false),
  useRocketIconForReblogs: v.fallback(v.boolean(), false),
  useHeartIconForFavourites: v.fallback(v.boolean(), false),
  greentext: v.fallback(v.boolean(), false),
  displayFqn: v.fallback(v.boolean(), true),
  driveViewMode: v.fallback(v.picklist(['grid', 'list']), 'grid'),
  skipInteractAsConfirmation: v.fallback(v.boolean(), false),
  showFilteredStatusAuthor: v.fallback(v.boolean(), false),
  filters: filteredArray(filterSchema),
  fitScrollTopButtonInHeader: v.fallback(v.boolean(), true),
  invertColumnsOrder: v.fallback(v.boolean(), false),
  sidebarNavigationDense: v.fallback(v.boolean(), false),

  pluraldawn: v.optional(
    coerceObject({
      enabled: v.fallback(v.boolean(), false),
    }),
  ),

  openshock: v.optional(
    coerceObject({
      baseUrl: v.string(),
      token: v.string(),
      defaultDevice: v.string(),
      hooks: filteredArray(openshockHookSchema),
    }),
  ),

  theme: v.optional(
    coerceObject({
      brandColor: v.optional(v.string()),
      accentColor: v.optional(v.string()),
      colors: v.optional(v.any()),
      interfaceSize: v.fallback(v.picklist(['sm', 'md', 'lg', 'xl']), 'md'),
      borderRadiusIntensity: v.fallback(v.picklist(['none', 'reduced', 'default']), 'default'),
      bordered: v.fallback(v.boolean(), false),
      grayscale: v.fallback(v.boolean(), false),
      contrast: v.fallback(v.picklist(['low', 'normal', 'high', 'max']), 'normal'),
      systemDarkThemePreference: v.fallback(v.picklist(['dark', 'black']), 'black'),
    }),
    undefined,
  ),

  systemFont: v.fallback(v.boolean(), false),
  systemEmojiFont: v.fallback(v.boolean(), false),
  demetricator: v.fallback(
    v.pipe(
      v.any(),
      v.transform((value) =>
        value === true || value === 'on' ? 'on' : value === 'always' ? 'always' : 'off',
      ),
      v.picklist(['off', 'on', 'always', 'fully_hide']),
    ),
    'off',
  ),
  demetricatorEmojiException: v.fallback(v.boolean(), false),

  chats: coerceObject({
    mainWindow: v.optional(v.picklist(['minimized', 'open']), 'minimized'),
    sound: v.optional(v.boolean(), true),
  }),

  timelines: filteredRecord(
    v.picklist([
      'home',
      'antenna',
      'bubble',
      'circle',
      'local',
      'group',
      'hashtag',
      'list',
      'public',
      'wrenched',
    ]),
    timelineFiltersSchema,
  ),

  account_timeline: coerceObject({
    shows: coerceObject({
      pinned: v.optional(v.boolean(), true),
    }),
  }),

  remote_timeline: coerceObject({
    pinnedHosts: v.optional(v.array(v.string()), []),
  }),

  threads: coerceObject({
    displayMode: v.optional(v.picklist(['tree', 'tree-indent', 'linear']), 'tree'),
  }),

  notifications: coerceObject({
    hideBots: v.optional(v.boolean(), false),
    quickFilter: coerceObject({
      active: v.optional(
        v.picklist(['all', 'mention', 'favourite', 'reblog', 'poll', 'status', 'follow', 'events']),
        'all',
      ),
      advanced: v.optional(v.boolean(), false),
      show: v.optional(v.boolean(), true),
    }),
    sounds: v.optional(v.record(v.string(), v.boolean()), {}),
    autoMarkRead: v.optional(v.boolean(), true),
  }),

  frequentlyUsedEmojis: v.fallback(v.record(v.string(), v.number()), {}),
  frequentlyUsedLanguages: v.fallback(v.record(v.string(), v.number()), {}),

  saved: v.fallback(v.boolean(), true),

  demo: v.fallback(v.boolean(), false),

  navigationItems: v.fallback(filteredArray(navigationItemSchema), DEFAULT_NAVIGATION_ITEMS),
  pinnedNavigationItems: v.fallback(
    filteredArray(navigationItemSchema),
    DEFAULT_PINNED_NAVIGATION_ITEMS,
  ),
  statusActionBarItems: v.fallback(
    v.array(v.picklist(AVAILABLE_STATUS_ACTION_BAR_ITEMS)),
    DEFAULT_STATUS_ACTION_BAR_ITEMS,
  ),
  quickReactionEmojis: v.fallback(v.array(v.string()), []),
  sidebarItems: v.fallback(filteredArray(sidebarItemSchema), DEFAULT_SIDEBAR_ITEMS),

  deck: deckSettingsSchema,
});

type DeckColumn = v.InferOutput<typeof deckColumnSchema>;
type DeckLayout = v.InferOutput<typeof deckLayoutSchema>;
type Settings = v.InferOutput<typeof settingsSchema>;
type TimelineFilters = Settings['timelines']['home'];

export {
  settingsSchema,
  openshockHookSchema,
  type NavigationItem,
  type SidebarItem,
  type DeckColumn,
  type DeckLayout,
  type Settings,
  type TimelineFilters,
  AVAILABLE_NAVIGATION_ITEMS,
  AVAILABLE_SIDEBAR_ITEMS,
  AVAILABLE_STATUS_ACTION_BAR_ITEMS,
  DEFAULT_NAVIGATION_ITEMS,
  DEFAULT_PINNED_NAVIGATION_ITEMS,
  DEFAULT_STATUS_ACTION_BAR_ITEMS,
  DEFAULT_SIDEBAR_ITEMS,
};
